import { LinkedClient } from '../common/linked-clients/types';
import { FeeItem } from '../common/tariff/types';
import {
  Questionnarie,
  OrganizationType,
  CustomerStatus,
  SignatureType,
  AccessRight,
  NewApplicationStatus,
  CofStatus,
  UserAccountProps,
} from '../common/types';
import { NatureOfControlEnum } from '../new-application/types';

export enum CorporateCustomerEntityForm {
  LTD = 'LTD',
  PLC = 'PLC',
  LP = 'LP',
  LLP = 'LLP',
}

export interface CorporateGeneralInfo {
  id?: string;
  name: string;
  registrationNumber: string;
  applicationId?: string;
  owner: { firstName: string; lastName: string };
  status?: CustomerStatus;
  statusChangeReason?: string;
  sic: string;
  countryOfIncorporated: string;
  totalBalance: number;
  currency: string;
  currencies: string[];
  cards: string[];
  accounts?: UserAccountProps[];
  risk: number;
  tariff?: string;
  tariffId?: string;
  tariffRiskScoreId?: string;
  fees?: FeeItem[];
  defaultFees?: FeeItem[];
  monthlyMaxValueIncomingPaymentRangeId?: string;
  monthlyMaxValueOutgoingPaymentRangeId?: string;
  monthlyNumberIncomingTransactionsRangeId?: string;
  monthlyNumberOutgoingTransactionsRangeId?: string;
  monthlyTurnoverRangeId?: string;
  monthlyValueIncomingTransactionsRangeId?: string;
  monthlyValueOutgoingTransactionsRangeId?: string;
  merchantId?: string;
  vatNumber?: string;
  questionnaire?: Questionnarie;
  amlCtfComplianceProgram?: boolean | string | null;
  requiredCurrenciesCryptoId?: string[];
  purposeOfAccount?: string;
  expectedTypeOfClients?: string[];
  clientScheme?: { name: string; id: string };
  pspIntegrationOrganization?: Partial<LinkedClient>;
  type?: OrganizationType;
  officials?: {
    companyOfficialType: string;
    status: string;
    user: { profile: { firstName: string; lastName: string } };
  }[];
}

export interface CorporateOverviewGeneral {
  companyNumber: string;
  companyName: string;
  entityForm: CorporateCustomerEntityForm;
  dateOfIncorporation: string;
  countryOfIncorporationId: string;
  sicCodes: string[];
  mccCode: string;
  status: CustomerStatus;
  correspondentAddress: string;
  registrationAddress: string;
  businessAddress: string;
  websiteLink: string;
  businessDescription: string;
  correspondenceAddressSameAsRegisteredAddress: boolean;
  businessAddressSameAsRegisteredAddress: boolean;
  correspondenceCountryId?: string | null;
  correspondenceCity?: string | null;
  correspondencePostalCode?: string | null;
  correspondenceAddress1?: string | null;
  correspondenceAddress2?: string | null;
  businessCountryId?: string | null;
  businessCity?: string | null;
  businessPostalCode?: string | null;
  businessAddress1?: string | null;
  businessAddress2?: string | null;
  registeredCountryId?: string;
  registeredCity?: string;
  registeredPostalCode?: string;
  registeredAddress1?: string;
  registeredAddress2?: string;
  productIds: string[];
  merchantId?: string;
  vatNumber?: string;
  purposeOfAccount?: string;
  expectedTypeOfClients?: string[];
  requiredCurrenciesCryptoId?: string[];
  amlCtfComplianceProgram?: string | boolean;
  pspIntegrationOrganization?: Partial<LinkedClient>;
}

export interface CustomerCompanyOfficial {
  companyOfficialType: string;
  createdAt: string;
  deletedAt: string;
  id: string;
  natureOfControl?: NatureOfControlEnum;
  organizationId: string;
  percentage: string;
  position: string;
  status: string;
  signatureType: SignatureType;
  havingControl?: boolean;
  accessRight: AccessRight;
  user: {
    status: string;
    profile: { firstName: string; lastName: string; dob: string };
  };
  userId: string;
}

export interface CofFormValues {
  organizationId: string;
  firstName: string;
  lastName: string;
  dob: string;
  position?: string;
  percentage?: string;
  natureOfControl?: NatureOfControlEnum;
  havingControl?: boolean;
}

export interface NewCofFormValues {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  dob: string;
  position: string;
  havingControl?: boolean;
  percentage?: string;
  natureOfControl?: string;
  signatureType?: string;
  companyOfficialType?: string;
  applicantId?: string;
  accessRight: AccessRight;
  applicationSchemeId?: string;
  nationalityId: string;
}

export interface CofProps {
  userId: string;
  organizationId: string;
  status: CofStatus;
  position: string;
  percentage: string;
  natureOfControl: NatureOfControlEnum;
  companyOfficialType: string;
  signatureType: SignatureType;
  accessRight: AccessRight;
  rejectionReason: string;
  archivedAt: string;
  rejectedAt: string;
  approvedAt: string;
  archivedBy: string;
  rejectedBy: string;
  approvedBy: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  havingControl?: boolean;
  officialApplication?: {
    id: string;
    status: NewApplicationStatus;
    type: string;
    firstName: string;
    lastName: string;
  };
  user: {
    status: string;
    totpEnabled: string;
    serviceRole: string[];
    organization?: {
      id?: string;
    };
    profile: {
      firstName: string;
      lastName: string;
      dob: string;
      id: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: string;
    };
  };
}

export interface CorporateQuestionnarieFormValues {
  typeOfBusinessLicence?: string;
  hmrcMlrNumberAndExpiryDate?: string;
  fcaNumber?: string;
  icoNumber?: string;
  numberOfBranches?: string;
  numberOfAgents?: string;
  hmrcMlrNumber?: string;
  hmrcRegistrationExpiryDate?: string;
  mlroDetails?: string;
  complianceConsultantDetails?: string;
  accountantDetails?: string;
  itProviderDetails?: string;
  payoutPartnerDetails?: string;
  hasTransactionMonitoring?: string | boolean;
  transactionMonitoringKind?: string;
  hasScreening?: string | boolean;
  screeningKind?: string;
  hasRegulatoryInvestigation?: string | boolean;
  regulatoryInvestigationDetails?: string;
  visitedByAuthorities?: string;
  visitedByAuthoritiesDetails?: string;
  hasOnboardCorporateClients?: string | boolean;
  hasOnboardMsb?: string | boolean;
  hasPaymentsForCharities?: string | boolean;
  hasChangesInManagementOrOwnership?: string | boolean;
  changesInManagementOrOwnershipDetails?: string;
  hasWorkingWithAnyOtherIpsp?: string | boolean;
  workingWithAnyOtherIpspDetails?: string;
  hasAuditedByExternalCompliance?: string | boolean;
  auditedByExternalComplianceDetails?: string;
  contactPersonName?: string;
  phoneNumber?: string;
  faxNumber?: string;
  contactEmail?: string;
  legalEntityIdCode?: string;
  isPepOrAssociate?: string | boolean;
  nameOfPep?: string;
  relToCustomerId?: string;
  relToPep?: string;
  orgEmployerAndPosition?: string;
  positionHeldCountryId?: string;
  politicalFunctionPeriodFrom?: string;
  politicalFunctionPeriodTo?: string;
  abilityMoveGovernmentFunds?: boolean | string;
  abilityMoveGovernmentFundsDetails?: string;
  belongsToGroup?: boolean | string;
  parentCompanyName?: string;
  parentCompanyCountryId?: string;
  subsidiariesInfo?: string;
  mainActivities?: string;
  groupWealthSourceIds?: string[];
  estimatedTotalWealthEuro?: string | number;
  customerAppliesKycProcedures?: string | boolean;
  hasBusinessActivity?: string | boolean;
  businessActivitiesIds?: string[];
  customersMainClientsIds?: string[];
  customersMainClientsOther?: string;
  salesChannelsIds?: string[];
  salesChannelsOther?: string;
  numberOfEmployees?: string | number;
  hasAccountsInOtherFinancialInstitutions?: string | boolean;
  otherFinancialInstitutions?: string;
  sourcesOfFundsIds?: string[];
  sourcesOfFundsLoanBusinessPartner?: string;
  sourcesOfFundsLoanCreditInstitution?: string;
  sourcesOfFundsOther?: string;
  sourceOfWealthIds?: string[];
  sourceOfWealthOther?: string;
  estimatedTotalWealth?: string | number;
  annualIncome?: string | number;
  sourcesOfAnnualIncomes?: {
    sourceOfAnnualIncomeId: string;
    percentage?: string | number;
  }[];
  sourcesOfAnnualIncomesOther?: string;
  accountTypeIds?: string[];
  tradingName?: string;
  questionnairesAccountsCurrencies?: string;
  questionnairesAccountsFlows?: string;
  questionnairesExpectedFundsOrigins?: string;
  groupWealthSourceOther?: string;
}
